import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiBarChart2, FiX } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const TABS = [
  { key: 'trial_balance', label: 'Trial Balance' },
  { key: 'income_statement', label: 'Income Statement' },
  { key: 'balance_sheet', label: 'Balance Sheet' },
  { key: 'cash_flow_statement', label: 'Cash Flow' },
];

const FinancialReports = () => {
  const [entities, setEntities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [tab, setTab] = useState('trial_balance');
  // USD by default: every line converted at its own entry's rate, so TRY,
  // USD and ZAR postings add up properly. 'functional' shows the company's
  // own currency (TRY for Turkey), the one its accountant files in.
  const [showIn, setShowIn] = useState('USD');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    financeAPI.entities.list().then((res) => setEntities(res.data.results || res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!entityId) { setPeriods([]); setPeriodId(''); return; }
    financeAPI.periods.list({ entity: entityId }).then((res) => setPeriods(res.data.results || res.data || [])).catch(() => {});
  }, [entityId]);

  const load = useCallback(async () => {
    if (!periodId) return;
    setLoading(true);
    setError(null);
    // Clear stale data immediately: `tab` can change synchronously (a click)
    // well before this async fetch resolves, and rendering e.g. the balance
    // sheet's JSX against a still-in-state trial-balance response (different
    // shape entirely) crashes on the first .map() call. Null forces the
    // loading branch until the report matching the CURRENT tab arrives.
    setReport(null);
    try {
      let res;
      const params = { currency: showIn };
      if (tab === 'trial_balance') res = await financeAPI.periods.trialBalance(periodId, params);
      else if (tab === 'income_statement') res = await financeAPI.periods.incomeStatement(periodId, params);
      else if (tab === 'balance_sheet') res = await financeAPI.periods.balanceSheet(periodId, params);
      else res = await financeAPI.periods.cashFlowStatement(periodId, params);
      setReport(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to generate this report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [periodId, tab, showIn]);

  useEffect(() => { load(); }, [load]);

  // What a converted row was actually posted in, e.g. "TRY 14,750.00 · USD 300.00".
  // Shown only when it adds something: more than one currency, or one that
  // isn't the currency the report is in.
  const postedIn = (byCurrency) => {
    if (!report || !byCurrency || byCurrency.length === 0) return null;
    if (byCurrency.length === 1 && byCurrency[0].currency === report.currency) return null;
    return byCurrency.map((c) => `${c.currency} ${c.amount}`).join(' · ');
  };

  const Row = ({ label, value, bold, detail }) => (
    <div style={{ padding: '8px 0', borderBottom: `1px solid ${fs.colors.cardBorder}`, fontWeight: bold ? 700 : 400 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: bold ? fs.colors.textBright : fs.colors.textDim }}>{label}</span>
        <span style={{ color: bold ? fs.colors.textBright : fs.colors.text }}>{value}</span>
      </div>
      {detail && <div style={{ fontSize: 11, color: fs.colors.textFaint, marginTop: 2 }}>posted as {detail}</div>}
    </div>
  );

  const renderTrialBalance = () => (
    <div style={fs.tableWrap}>
      <table style={fs.table}>
        <thead><tr style={fs.tableHeadRow}>{['Account', 'Name', 'Debit', 'Credit'].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
        <tbody>
          {report.rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #111' }}>
              <td style={{ ...fs.td, fontFamily: 'monospace' }}>{r.account_code}</td>
              <td style={{ ...fs.td, color: fs.colors.text }}>
                {r.account_name}
                {postedIn(r.by_currency) && <div style={{ fontSize: 11, color: fs.colors.textFaint }}>posted as {postedIn(r.by_currency)}</div>}
              </td>
              <td style={fs.td}>{r.debit !== '0.00' ? r.debit : ''}</td>
              <td style={fs.td}>{r.credit !== '0.00' ? r.credit : ''}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...fs.td, fontWeight: 700, color: fs.colors.textBright }} colSpan={2}>TOTAL</td>
            <td style={{ ...fs.td, fontWeight: 700, color: fs.colors.textBright }}>{report.total_debit}</td>
            <td style={{ ...fs.td, fontWeight: 700, color: fs.colors.textBright }}>{report.total_credit}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ padding: '10px 14px', fontSize: 12, color: report.is_balanced ? fs.colors.green : fs.colors.red }}>
        {report.is_balanced ? '✓ Balanced' : '✗ Not balanced — this should never happen; the ledger core guarantees it.'}
      </div>
    </div>
  );

  const renderIncomeStatement = () => (
    <div style={{ ...fs.card, maxWidth: 560 }}>
      <div style={{ ...fs.label, marginBottom: 4 }}>Revenue</div>
      {report.revenue.map((r, i) => <Row key={i} label={`${r.account_code} — ${r.account_name}`} value={r.amount} detail={postedIn(r.by_currency)} />)}
      <Row label="Total Revenue" value={report.total_revenue} bold />
      <div style={{ ...fs.label, margin: '16px 0 4px' }}>Expenses</div>
      {report.expenses.map((r, i) => <Row key={i} label={`${r.account_code} — ${r.account_name}`} value={r.amount} detail={postedIn(r.by_currency)} />)}
      <Row label="Total Expenses" value={report.total_expenses} bold />
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: `2px solid ${fs.colors.cardBorder}` }}>
        <Row label="Net Income" value={report.net_income} bold />
      </div>
    </div>
  );

  const renderBalanceSheet = () => (
    <div style={{ ...fs.card, maxWidth: 560 }}>
      <div style={{ ...fs.label, marginBottom: 4 }}>Assets</div>
      {report.assets.map((r, i) => <Row key={i} label={`${r.account_code} — ${r.account_name}`} value={r.balance} detail={postedIn(r.by_currency)} />)}
      <Row label="Total Assets" value={report.total_assets} bold />
      <div style={{ ...fs.label, margin: '16px 0 4px' }}>Liabilities</div>
      {report.liabilities.map((r, i) => <Row key={i} label={`${r.account_code} — ${r.account_name}`} value={r.balance} detail={postedIn(r.by_currency)} />)}
      <Row label="Total Liabilities" value={report.total_liabilities} bold />
      <div style={{ ...fs.label, margin: '16px 0 4px' }}>Equity</div>
      {report.equity.map((r, i) => <Row key={i} label={`${r.account_code} — ${r.account_name}`} value={r.balance} detail={postedIn(r.by_currency)} />)}
      <Row label="Retained Earnings (computed)" value={report.retained_earnings} />
      <Row label="Total Equity" value={report.total_equity} bold />
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: `2px solid ${fs.colors.cardBorder}` }}>
        <Row label="Total Liabilities + Equity" value={report.total_liabilities_and_equity} bold />
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: report.is_balanced ? fs.colors.green : fs.colors.red }}>
        {report.is_balanced ? '✓ Assets = Liabilities + Equity' : '✗ Does not balance'}
      </div>
    </div>
  );

  const renderCashFlow = () => (
    <div style={{ ...fs.card, maxWidth: 560 }}>
      <Row label="Beginning Cash" value={report.beginning_cash} />
      <Row label="Operating Activities" value={report.operating} />
      <Row label="Investing Activities" value={report.investing} />
      <Row label="Financing Activities" value={report.financing} />
      {parseFloat(report.uncategorized) !== 0 && <Row label="Uncategorized" value={report.uncategorized} />}
      <Row label="Net Change in Cash" value={report.net_change_in_cash} bold />
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: `2px solid ${fs.colors.cardBorder}` }}>
        <Row label="Ending Cash" value={report.ending_cash} bold />
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: report.reconciles ? fs.colors.green : fs.colors.red }}>
        {report.reconciles ? '✓ Reconciles' : '✗ Does not reconcile'}
      </div>
    </div>
  );

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiBarChart2 size={22} />Financial Reports</h1>
          <p style={fs.pageSubtitle}>Generated live from the ledger — nothing here is hand-maintained.</p>
        </div>
        <button onClick={load} title="Refresh" style={fs.iconButton}>
          <FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={entityId} onChange={(e) => setEntityId(e.target.value)} style={fs.select}>
          <option value="">Select entity…</option>
          {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} style={fs.select} disabled={!entityId}>
          <option value="">Select period…</option>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={showIn} onChange={(e) => setShowIn(e.target.value)} style={fs.select} title="Currency the report is shown in">
          <option value="USD">Show in USD</option>
          <option value="functional">Show in the company's own currency</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {TABS.map((t) => (
          <button
            key={t.key} onClick={() => { setReport(null); setTab(t.key); }}
            style={{
              padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${tab === t.key ? fs.colors.purple : fs.colors.cardBorder}`,
              background: tab === t.key ? 'rgba(1, 31, 243,0.15)' : fs.colors.card,
              color: tab === t.key ? fs.colors.purple : '#888',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {!periodId ? (
        <div style={fs.emptyState}><FiBarChart2 size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>Pick an entity and period to generate a report.</div></div>
      ) : loading ? (
        <div style={fs.loadingState}>Generating…</div>
      ) : report ? (
        <>
          <div style={{ fontSize: 12, color: fs.colors.textFaint, marginBottom: 10 }}>
            All amounts in {report.currency}, each entry converted at the rate on the day it was posted.
          </div>
          {tab === 'trial_balance' && renderTrialBalance()}
          {tab === 'income_statement' && renderIncomeStatement()}
          {tab === 'balance_sheet' && renderBalanceSheet()}
          {tab === 'cash_flow_statement' && renderCashFlow()}
        </>
      ) : null}
    </div>
  );
};

export default FinancialReports;
